/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import { Parameter } from "lib/parameters/types";
import cn from "classnames";
import styles from "gui/embed/embed.module.css";
import { Cell } from "gui/grid/cell";
import { Grid } from "gui/grid/grid";
import { Icon } from "gui/icon/icon";
import { Loading } from "gui/loading/loading";
import { Subtitle } from "gui/subtitle/subtitle";
import { VisualItem } from "gui/visual/visual";
import { queryLink } from "lib/links/links";
import { useEmbedVisual } from "lib/visuals/hooks";
import { useQueryResult } from "lib/results/useQueryResult";
import { useSessionShowWatermark } from "gui/session/session";
import { useTheme } from "gui/theme/theme";
import { parseParametersQuery } from "lib/parameters/query";
import { browserQueryString } from "lib/links/query";
import { mergeParameters } from "lib/parameters/parameters";
import { QueryVisual } from "lib/visuals/types";
import { parseOwner } from "lib/entries/entries";
import { NotFoundPage } from "pages/404";
import { VisualNotFoundError } from "lib/visuals/visuals";

// A component for the SanityEmbed page section type.
export const Embed: React.FC<{
  link: string;
  icon?: string;
  title?: string;
  transparent?: boolean;
  revealOnScroll?: boolean;
  watermark: boolean;
}> = (props) => {
  const parsed = parseEmbedLink(props.link);

  return (
    <Grid revealOnScroll={props.revealOnScroll} gaps>
      {props.title && (
        <Subtitle icon={props.icon} text={props.title}>
          <a href={queryLink(parsed.query, parsed.visual)}>
            ...see SQL query
            <Icon icon="arrow-right" />
          </a>
        </Subtitle>
      )}
      <Cell className={styles.section}>
        <EmbedVisual
          {...parsed}
          watermark={props.watermark}
          transparent={props.transparent}
        />
      </Cell>
    </Grid>
  );
};

// A component that renders an embedded visualization.
export const EmbedVisual: React.FC<{
  query: number;
  visual: number;
  apiKey?: string;
  watermark?: boolean;
  transparent?: boolean;
  iframe?: boolean;
}> = (props) => {
  const { result, visual, error } = useEmbed(
    props.query,
    props.visual,
    props.apiKey
  );
  const sessionShowWatermark = useSessionShowWatermark();

  const className = cn(styles.visual, props.iframe && styles.iframe);

  const theme = useTheme();
  const wrapperStyle = { background: theme.background_color };

  if (error && error instanceof VisualNotFoundError) {
    return <NotFoundPage />;
  }

  if (!result || !visual) {
    return (
      <div className={className} style={wrapperStyle}>
        <Loading />
      </div>
    );
  }

  // Use props.watermark if it is explicitly set to true or false.
  // Check the permissions of the author if we're on the  embed page.
  // Otherwise, check the permission of the current session user.
  const watermark =
    typeof props.watermark === "boolean"
      ? props.watermark
      : props.iframe
      ? Boolean(visual.query_details?.show_watermark)
      : sessionShowWatermark;

  return (
    <div className={className} style={wrapperStyle}>
      <VisualItem
        visual={visual}
        result={result}
        queryId={visual.query_details!.query_id}
        queryName={visual.query_details!.name}
        queryDescription={visual.query_details!.description}
        owner={parseOwner(visual.query_details!)}
        watermark={watermark}
        transparent={props.transparent}
        iframe={props.iframe}
      />
    </div>
  );
};

// getParams extracts the default params from the query
// and merges them with the overrides that may be specified in the
// browser url
const getParams = (visual?: QueryVisual): Parameter[] => {
  const defaults = visual?.query_details?.parameters || [];

  return mergeParameters(
    defaults,
    parseParametersQuery(defaults, browserQueryString())
  );
};

const useEmbed = (queryId: number, visualId: number, apiKey?: string) => {
  const visual = useEmbedVisual(visualId, apiKey);
  const parameters = getParams(visual.data);
  const result = useQueryResult(queryId, parameters, {
    can_refresh: parameters.every((p) => p.value === p.default_value),
    apiKey,
  });

  if (visual.error) {
    if (visual.error instanceof VisualNotFoundError) {
      return { error: visual.error };
    } else {
      throw visual.error;
    }
  }

  if (!visual.data || !result) {
    return {};
  }

  if (!visual.data.query_details) {
    throw new Error("missing embed visual query details");
  }

  if (visual.data.query_details.query_id !== queryId) {
    throw new Error("embed visual query id mismatch");
  }

  return {
    visual: visual.data,
    result,
  };
};

// Extract the query id, visualization id, and API key from an embed link.
// Supports both the previous Redash embed link format and the new link format.
export const parseEmbedLink = (link: string) => {
  const match = link.match(
    link.includes("/embeds/")
      ? new RegExp("/embeds/(\\d+)/(\\d+)/?([^/?#]+)?")
      : new RegExp("/query/(\\d+)/visualization/(\\d+)\\?api_key=([^&#]+)")
  );

  if (match?.length !== 4) {
    throw new Error(`invalid embed link: ${link}`);
  }

  return {
    query: parseInt(match[1], 10),
    visual: parseInt(match[2], 10),
    apiKey: match[3],
  };
};
